import React, { useEffect, useState } from 'react';
import {
  Box,
  TableContainer,
  TableHead,
  Table,
  TableCell,
  TableBody,
  MenuItem,
  TableRow,
  Button,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import { AppDispatch, RootState } from '../../../../../../Redux/store';
import { SearchBar, StyledTextField } from '../../../../../../styles/textField';
import { StyledSelect, menuItemCSS } from '../../../../../../styles/select';
import { SubLabel } from '../../../../../../styles/sublabel';
import {
  buttonIconStyles,
  mediumIconStyles,
} from '../../../../../../styles/icon';
import {
  zeroPaddingAutoWidthButton,
  cyanBlueBackgroundButtonStyles,
} from '../../../../../../styles/button';
import ShopifyAddGroupDrawer from './addNewGroup';
import {
  clearGroupSettings,
  updateClose,
  updateGroupConfiguration,
} from '../../../../../../Redux/Slices/Application/AppConfiguration/Group/group';
import {
  addNewUser,
  removeUser,
  updatePassword,
  updateUserName,
  updateGroup,
} from '../../../../../../Redux/Slices/Application/BasicSettings/basicSettingsSlice';
import { updateBasicSettings } from '../../../../../../Redux/Slices/Application/BasicSettings/updateApplicationHandler';
import { SmallHeading } from '../../../../../../styles/smallHeading';
import { deleteGroupConfiguration } from '../../../../../../Redux/Slices/Application/AppConfiguration/Group/groupHandler';
import CommonTable from '../../../../../../components/Table/CommonTable';
import { handleSearch } from '../../../../../../utils/Groups/groupUtility';
import { getAllGroups } from '../../../../../../Redux/Slices/Application/appSlice';

function ShopifyNonPlusUserConfiguration() {
  const dispatch = useDispatch<AppDispatch>();
  const { configuredGroups, app } = useSelector(
    (state: RootState) => state.application,
  );
  const { shopifyUsers } = useSelector(
    (state: RootState) => state.basicSettings,
  );

  const [filteredGroups, setFilteredGroups] = useState(configuredGroups);
  const [filteredShopifyUsers, setFilteredShopifyUsers] = useState(
    shopifyUsers,
  );

  useEffect(() => {
    dispatch(getAllGroups());
  }, []);

  useEffect(() => {
    setFilteredGroups(configuredGroups);
    setFilteredShopifyUsers(shopifyUsers);
  }, [configuredGroups, shopifyUsers]);

  const addNewGroup = () => {
    dispatch(clearGroupSettings());
    dispatch(updateClose(true));
  };

  const handleDeleteGroup = async (id: string) => {
    await dispatch(deleteGroupConfiguration({ id, app }));
  };

  const handleEditGroup = async (id: string) => {
    dispatch(updateClose(false));
    await configuredGroups.forEach(async (element: any) => {
      if (element._id === id) {
        await dispatch(updateGroupConfiguration(element));
        dispatch(updateClose(true));
      }
    });
  };

  const styles = {
    height: { xxl: '25rem', xl: '18rem', md: '16rem' },
  };
  const columnsToSearch = ['identifier', 'description'];
  const shopifyUsersColumnsToSearch = ['username', 'group_name'];

  return (
    <Box display="flex" width="100%" py="1.0rem" gap="1rem">
      <ShopifyAddGroupDrawer />
      <Box
        border="0.063rem solid #EBEBEB"
        borderRadius="0.625rem"
        width="50%"
        p="1rem"
      >
        <SmallHeading>Add Groups and Manage User Configuration</SmallHeading>
        <SubLabel>Add Groups to configure IAM Users</SubLabel>
        <Box display="flex" justifyContent="space-between" py="1.5rem">
          <SearchBar
            placeholder="Search for groups"
            onChange={(event: any) => {
              handleSearch(
                event,
                configuredGroups,
                setFilteredGroups,
                columnsToSearch,
              );
            }}
          />
          <Button
            sx={{ ...cyanBlueBackgroundButtonStyles, width: '8rem' }}
            onClick={addNewGroup}
          >
            <AddIcon sx={{ ...buttonIconStyles, color: 'white' }} />
            &nbsp; Add New
          </Button>
        </Box>
        <CommonTable
          data={filteredGroups}
          columns={[
            { field: 'identifier', headerName: 'Group Name' },
            { field: 'description', headerName: 'Description' },
          ]}
          styles={styles}
          onEdit={handleEditGroup}
          onDelete={handleDeleteGroup}
          pages="Group"
        />
      </Box>
      <Box
        border="0.063rem solid #EBEBEB"
        borderRadius="0.625rem"
        width="50%"
        p="1rem"
      >
        <SmallHeading>Shopify Staff User Mapping</SmallHeading>
        <SubLabel>Please Shopify Staff User Details</SubLabel>
        <Box display="flex" justifyContent="space-between" py="1.5rem">
          <SearchBar
            placeholder="Search for user details"
            onChange={(event: any) => {
              handleSearch(
                event,
                shopifyUsers,
                setFilteredShopifyUsers,
                shopifyUsersColumnsToSearch,
              );
            }}
          />
          <Button
            sx={{ ...cyanBlueBackgroundButtonStyles, width: '8rem' }}
            onClick={() => dispatch(addNewUser())}
          >
            <AddIcon sx={{ ...buttonIconStyles, color: 'white' }} />
            &nbsp; Add User
          </Button>
        </Box>
        <TableContainer sx={{ height: { xxl: '42vh', md: '38vh' } }}>
          <Table sx={{ border: 'none', boxShadow: 'none' }}>
            <TableHead>
              <TableCell sx={zeroPaddingAutoWidthButton}>IAM Email</TableCell>
              <TableCell sx={zeroPaddingAutoWidthButton}>
                Staff User Email
              </TableCell>
              <TableCell sx={zeroPaddingAutoWidthButton}>
                Staff User Password
              </TableCell>
              <TableCell sx={zeroPaddingAutoWidthButton} align="right">
                Action
              </TableCell>
            </TableHead>
            <TableBody>
              {filteredShopifyUsers?.map((element: any, index: number) => (
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{
                      paddingY: '0.5rem !important',
                    }}
                  >
                    <StyledSelect
                      fullWidth
                      value={element.group_name}
                      sx={{ width: '7rem !important' }}
                      onChange={(event: any) => dispatch(
                        updateGroup({
                          groupName: event.target.value,
                          index,
                        }),
                      )}
                    >
                      {configuredGroups.map((source: any) => (
                        <MenuItem
                          key={source.identifier}
                          value={source.identifier}
                          sx={menuItemCSS}
                        >
                          {source.identifier}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingY: '0.5rem !important',
                      paddingX: '0 !important',
                    }}
                  >
                    <StyledTextField
                      fullWidth
                      id={`board-name-${index}`}
                      size="small"
                      name="username"
                      variant="outlined"
                      onChange={(event: any) => dispatch(
                        updateUserName({
                          username: event.target.value,
                          index,
                        }),
                      )}
                      value={element.username}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingY: '0 !important',
                      paddingX: '0.5rem',
                    }}
                  >
                    <StyledTextField
                      id={`board-name-${index}`}
                      fullWidth
                      size="small"
                      name="password"
                      type="password"
                      disabled={element.encrypted}
                      variant="outlined"
                      onChange={(event: any) => dispatch(
                        updatePassword({
                          password: event.target.value,
                          index,
                        }),
                      )}
                      value={element.encrypted ? '********' : element.password}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: '0 !important' }}>
                    <DeleteOutlineOutlinedIcon
                      sx={mediumIconStyles}
                      onClick={() => dispatch(removeUser(index))}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Button
          variant="text"
          sx={cyanBlueBackgroundButtonStyles}
          onClick={() => dispatch(updateBasicSettings())}
        >
          Save Configuration
        </Button>
      </Box>
    </Box>
  );
}

export default ShopifyNonPlusUserConfiguration;
